import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import resourceService from "@/services/booking/services/resourceService";
import {
  BookingBookingViewModelGen,
  BookingCreateResourceDtoGen,
  BookingResourceControllerFindAllByPartnerIdParamsGen,
  BookingResourceViewModelGen,
  BookingUpdateResourceDtoGen
} from "@/services/booking/v1/data-contracts";
import { Action, getModule, Module, Mutation } from "vuex-module-decorators";
import { BasePagination, FilterTypes, IPaginationFilterListElement } from "./base-pagination.store";
import store from "../../store/VuexPlugin";

@Module({
  dynamic: true,
  namespaced: true,
  name: "resource",
  store
})
export class ResourceStore extends BasePagination<
  BookingResourceViewModelGen,
  BookingResourceControllerFindAllByPartnerIdParamsGen
> {
  protected _pageOrder: PageOrderEnum = PageOrderEnum.DESCENDING;
  protected _itemsPerPage = 100;
  protected _totalPages = 0;
  protected _paginationList: BookingResourceViewModelGen[] = [];
  protected _currentPage = 1;
  protected _totalItems = 0;
  protected _isLoadAll = false;
  protected _filterList: IPaginationFilterListElement[] = [
    { key: "displayName", type: FilterTypes.STRING },
    { key: "isActive", type: FilterTypes.BOOLEAN },
    { key: "partnerId", type: FilterTypes.OBJECT_ID },
    { key: "_id", type: FilterTypes.OBJECT_ID }
  ];

  private _resourceLookup: Map<string, BookingResourceViewModelGen> = new Map();

  /**
   * Gets all services in a lookup table
   */
  get resourceLookup() {
    return this._resourceLookup;
  }

  private _resource: BookingResourceViewModelGen | null = null;
  private _booking: BookingBookingViewModelGen | null = null;
  private _bookings: BookingResourceViewModelGen[] = [];

  get resource(): BookingResourceViewModelGen | null {
    return this._resource;
  }

  get booking(): BookingBookingViewModelGen | null {
    return this._booking;
  }

  get bookings(): BookingResourceViewModelGen[] {
    return this._bookings;
  }

  @Mutation
  mutateResource(resource: BookingResourceViewModelGen) {
    this._resource = resource;
  }

  @Mutation
  mutateBooking(booking: BookingBookingViewModelGen) {
    this._booking = booking;
  }

  @Mutation
  mutateBookings(bookings: BookingResourceViewModelGen[]) {
    this._bookings.splice(0, this._bookings.length, ...bookings);
  }

  @Action
  protected async loadDocuments(query: BookingResourceControllerFindAllByPartnerIdParamsGen): Promise<any> {
    const resources = await resourceService.findBookingsByPartner(query);

    this.context.commit("mutateSResourceLookup", resources.data);

    return resources;
  }

  @Mutation
  mutateSResourceLookup(resources: BookingResourceViewModelGen[]) {
    this._resourceLookup = new Map(resources.map(obj => [obj.id, obj]));
  }

  @Action
  async create(data: { partnerId: string; data: BookingCreateResourceDtoGen }): Promise<BookingResourceViewModelGen> {
    const resource = await resourceService.create(data.partnerId, data.data);
    this.context.commit("mutateResource", resource || null);
    return resource;
  }

  @Action
  async findOneByPartnerId(data: { partnerId: string; id: string }): Promise<BookingResourceViewModelGen> {
    const resource = await resourceService.findResourceByPartner(data.partnerId, data.id);
    this.context.commit("mutateResource", resource || null);
    return resource;
  }

  @Action
  async replaceOneByPartnerId(data: {
    partnerId: string;
    id: string;
    data: BookingUpdateResourceDtoGen;
  }): Promise<BookingResourceViewModelGen> {
    const resource = await resourceService.replaceOneByPartnerId(data.partnerId, data.id, data.data);
    this.context.commit("mutateResource", resource || null);
    this.replaceInList(resource);
    return resource;
  }

  @Action
  async removeOneByPartnerId(data: { partnerId: string; id: string }): Promise<BookingResourceViewModelGen> {
    const resource = await resourceService.removeResource(data.partnerId, data.id);
    this.context.commit("mutateResource", resource || null);
    return resource;
  }
}

export const ResourceModule = getModule(ResourceStore);
